import {
  Button,
  EButtonColorVariant,
  EHeadingStyleVariant,
  EModalFooterVariants,
  EModalSize,
  Heading,
  LinkButton,
  Modal,
} from '@outdoorsyco/bonfire';
import { clsx } from 'clsx';
import React, { useMemo } from 'react';
import { useIntl } from 'react-intl';

import DeliverySelector from './DeliverySelector';
import EstimatedTripDistanceSelector from './EstimatedTripDistanceSelector';
import { GuidedSearchComponent } from './GuidedSearchComponent';
import { useGuidedSearchContext } from './GuidedSearchContext';
import TowableVehicleSelector from './TowableVehicleSelector';
import { useGuidedSearchDeliveryInfo } from './useGuidedSearchDeliveryInfo';
import { useGuidedSearchTripDistanceInfo } from './useGuidedSearchTripDistanceInfo';
import { useGuidedSearchVehicleInfo } from './useGuidedSearchVehicleInfo';

type TGuidedSearchModalProps = {
  isOpen: boolean;
  onClose: () => void;
  className?: string;
};

export const GuidedSearchModal = ({ isOpen, onClose, className }: TGuidedSearchModalProps) => {
  const intl = useIntl();

  const {
    goToPrevStep,
    goToNextStep,
    applySelection,
    currentStep,
    numberOfSteps,
    currentStepComponent,
    currentStepSelection,
    updateCurrentStepSelection,
    hasTowSelected,
    hasDeliverySelected,
    hasDriveSelected,
  } = useGuidedSearchContext();

  const { vehicleInfo, handleVehicleChange, cancelPendingApply } = useGuidedSearchVehicleInfo(() =>
    applySelection(),
  );
  const { deliveryInfo, handleDeliveryChange, isDeliveryFullySelected } =
    useGuidedSearchDeliveryInfo();
  const { tripDistanceInfo, handleTripDistanceChange } = useGuidedSearchTripDistanceInfo();

  const everyStepSelectionApplied = useMemo(() => {
    if (!currentStepComponent || !currentStepSelection) return false;
    return currentStepComponent.fields.every(field => currentStepSelection[field.id]);
  }, [currentStepComponent, currentStepSelection]);

  if (!currentStepComponent) return null;

  return (
    <Modal
      isOpen={isOpen}
      onClose={() => {
        onClose();
        cancelPendingApply();
        applySelection();
      }}
      size={EModalSize.Medium}
      className={className}>
      <Modal.Actions />

      <Modal.Header>
        <Heading variant={EHeadingStyleVariant.H5} className="text-center">
          {currentStepComponent.properties.title}
        </Heading>

        <div className="relative mx-0 my-6 md:mx-8 h-[4px]">
          <div className="absolute inset-0 bg-neutral-20" />

          <div
            className="absolute top-0 bottom-0 left-0 bg-green-500"
            style={{ width: `${(currentStep / numberOfSteps) * 100}%` }}
          />
        </div>
      </Modal.Header>

      <Modal.Content>
        <GuidedSearchComponent
          {...currentStepComponent}
          values={currentStepSelection || {}}
          onChange={updateCurrentStepSelection}
        />

        {hasTowSelected && (
          <div
            className="overflow-hidden transition-all duration-300 ease-in-out"
            style={{
              animation: 'guidedSearchSlideDown 300ms ease-in-out',
            }}>
            <TowableVehicleSelector
              key="towable-vehicle-selector"
              onVehicleChange={handleVehicleChange}
              initialVehicleInfo={vehicleInfo}
            />
          </div>
        )}

        {hasDeliverySelected && (
          <div
            className="overflow-hidden transition-all duration-300 ease-in-out"
            style={{
              animation: 'guidedSearchSlideDown 300ms ease-in-out',
            }}>
            <DeliverySelector
              key="delivery-selector"
              onDeliveryChange={handleDeliveryChange}
              initialDeliveryInfo={deliveryInfo}
            />
          </div>
        )}

        {hasDriveSelected && (
          <div
            className="overflow-hidden transition-all duration-300 ease-in-out"
            style={{
              animation: 'guidedSearchSlideDown 300ms ease-in-out',
            }}>
            <EstimatedTripDistanceSelector
              key="estimated-trip-distance-selector"
              onTripDistanceChange={handleTripDistanceChange}
              initialTripDistanceInfo={tripDistanceInfo}
            />
          </div>
        )}
        <style jsx>{`
          @keyframes guidedSearchSlideDown {
            from {
              opacity: 0;
              max-height: 0;
              transform: translateY(-10px);
            }
            to {
              opacity: 1;
              max-height: 1000px;
              transform: translateY(0);
            }
          }
        `}</style>
      </Modal.Content>

      <Modal.Footer variant={EModalFooterVariants.Actions}>
        <LinkButton
          label={intl.formatMessage({ defaultMessage: 'Back', id: 'cyR7Kh' })}
          className={clsx({ invisible: !goToPrevStep })}
          onClick={goToPrevStep}
        />

        <Button
          label={intl.formatMessage({ defaultMessage: 'Next', id: '9+Ddtu' })}
          variant={EButtonColorVariant.Primary}
          onClick={() => {
            cancelPendingApply();
            if (hasTowSelected || hasDeliverySelected || hasDriveSelected) {
              applySelection();
            } else {
              (goToNextStep || applySelection)();
            }
          }}
          disabled={!everyStepSelectionApplied || (hasDeliverySelected && !isDeliveryFullySelected)}
        />
      </Modal.Footer>
    </Modal>
  );
};
